"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cacheStatusTTS } from "@/app/lib/api";
import type { TTSContext } from "@/app/lib/types";

// Per-target cache-status tracker — drives the per-card play button's
// hollow-vs-solid icon. Server-side this maps to POST /tts/cache-status
// which does a stat()-only lookup, so probing is cheap. Each unique
// (recordId, providerId) tuple is probed at most once per chat-session
// view; results are kept in a module-local Map so navigating away and
// back doesn't re-probe.
//
// Voice / model / context match what useTTSPlayer.play() sends, so the
// cache key derived server-side is identical → consistent hit/miss
// answers across "status probe" and "actual play". Any drift here =
// solid-icon-then-cache-miss confusion.

const TTS_VOICE = "alloy";
const TTS_MODEL = "gpt-4o-mini-tts";
const TTS_CONTEXT: TTSContext = "one_off";

type CacheState = "unknown" | "miss" | "hit";

export interface TTSTargetKey {
  recordId: string;
  providerId: string;
  text: string;
}

function statusKey(recordId: string, providerId: string): string {
  return `${recordId}::${providerId}`;
}

export interface UseTTSCacheStatusReturn {
  /** Look up cached-status for a (recordId, providerId). Returns
   *  "unknown" until the probe lands, then "hit" or "miss". */
  status: (recordId: string, providerId: string) => CacheState;
  /** Tell the tracker about new targets to probe. Probes in the
   *  background; idempotent — already-probed targets are skipped. */
  observe: (targets: TTSTargetKey[]) => void;
  /** Mark a (recordId, providerId) as a hit immediately. Used after
   *  a successful TTS stream completes — the server just cached the
   *  bytes, so the play button can flip solid without waiting for
   *  the next probe. */
  markHit: (recordId: string, providerId: string) => void;
  /** Drop the tracker's state (e.g. on session switch). */
  reset: () => void;
}

export function useTTSCacheStatus(): UseTTSCacheStatusReturn {
  const [, forceRender] = useState(0);
  // Map kept in a ref (not state) so probe completions don't re-render
  // every consumer when a single entry updates — only the affected
  // card needs to flip. Consumers re-render via the forceRender bump.
  const cacheRef = useRef<Map<string, CacheState>>(new Map());
  // Targets currently being probed — avoid double-firing for the same key.
  const inflightRef = useRef<Set<string>>(new Set());

  const status = useCallback((recordId: string, providerId: string): CacheState => {
    return cacheRef.current.get(statusKey(recordId, providerId)) ?? "unknown";
  }, []);

  const observe = useCallback((targets: TTSTargetKey[]) => {
    let mutated = false;
    for (const target of targets) {
      const key = statusKey(target.recordId, target.providerId);
      if (cacheRef.current.has(key)) continue;
      if (inflightRef.current.has(key)) continue;
      inflightRef.current.add(key);
      // Fire-and-forget probe. Result update mutates cacheRef and
      // triggers a forceRender so consumers re-evaluate status().
      void cacheStatusTTS({
        text: target.text,
        voice: TTS_VOICE,
        model: TTS_MODEL,
        context: TTS_CONTEXT,
      }).then((hit) => {
        cacheRef.current.set(key, hit ? "hit" : "miss");
        inflightRef.current.delete(key);
        forceRender((n) => n + 1);
      });
      mutated = true;
    }
    if (mutated) forceRender((n) => n + 1);
  }, []);

  const markHit = useCallback((recordId: string, providerId: string) => {
    const key = statusKey(recordId, providerId);
    if (cacheRef.current.get(key) === "hit") return;
    cacheRef.current.set(key, "hit");
    forceRender((n) => n + 1);
  }, []);

  const reset = useCallback(() => {
    cacheRef.current.clear();
    inflightRef.current.clear();
    forceRender((n) => n + 1);
  }, []);

  // Cleanup on unmount — no listeners to remove, but reset the maps
  // so the next mount starts fresh.
  useEffect(() => () => reset(), [reset]);

  return { status, observe, markHit, reset };
}
